// Copyright © 2021 Move Closer

import { AnyObject } from '@movecloser/front-core'
import { Component } from 'vue-property-decorator'
import { HasDriver } from '@d24/demarked'

import { Related, RelatedType } from '../../services'

import { AbstractModuleUi, FetchesRelatedAsync } from '../_abstract/ui'
import { Addon, ModuleDriver } from '../_contracts'
import { ArticleHeader, ArticleHeaderData, LineHeader } from '../_components/ui'

import { RelatedArticlesModule } from './RelatedArticles.contracts'
import { CountsArticles } from '../_abstract/abstract-module'

/**
 * Component capable of rendering the `RelatedArticlesModule`'s contents.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
@Component<RelatedArticlesModuleUi>({
  name: 'RelatedArticlesModuleUi',
  components: {
    ArticleHeader,
    LineHeader
  },

  async created () {
    await this.fetchRelated()
  },

  template: `
    <ul class="related-articles list-unstyled">
    <li v-for="(article, i) in articles" :key="i" class="mt-9 mb-9">
      <ArticleHeader v-bind="{ content: article, eventBus, fallbackImage, shareService, windowService, isMobile, pdfService, parent, relatedService,
        isLazy: true, isRelated: true }"/>

      <router-link :to="article.urlPath" class="btn btn-primary related-articles__btn mt-5">
        {{ $t('_.read-all') }}
      </router-link>
    </li>
    </ul>
  `
})
export class RelatedArticlesModuleUi
  extends AbstractModuleUi<RelatedArticlesModule>
  implements CountsArticles, FetchesRelatedAsync {
  public articles: ArticleHeaderData[] = []

  /**
   * @inheritDoc
   */
  public get articlesCount (): number {
    return this.articles.length
  }

  /**
   * @inheritDoc
   */
  public async fetchRelated (): Promise<void> {
    const unresolvedArticles: Related<RelatedType.Content>[] =
      this.module.content.useParentRelated && this.parent && this.parent.hasProperty(Addon.RelatedArticles)
        ? this.parent.getProperty(Addon.RelatedArticles) as Related<RelatedType.Content>[]
        : this.module.content.articles

    const resolvedArticles: ArticleHeaderData[] = []
    await Promise.all(unresolvedArticles.map(unresolvedArticle => {
      return new Promise((resolve) => {
        return this.relatedService.resolve(unresolvedArticle, {})
          .then((article: AnyObject) => {
            resolvedArticles.push(
              {
                author: article.author,
                header: this.module.content.header.text || null,
                theme: null,
                ads: article.ads || null,
                cover: article.properties.cover,
                commentsCount: null,
                labels: [],
                lead: this.parseLead(article.properties.lead) || null,
                meta: article.properties.meta || null,
                options: article.contentProperties,
                contentProperties: {},
                parent: null,
                pdf: null,
                publicationDate: article.publicationDate,
                title: article.title,
                urlPath: article.urlPath
              }
            )

            resolve()
          }).catch(error => {
            if (this.debug) {
              console.error(error)
            }

            resolve()
          })
      })
    })).then(() => {
      this.articles = resolvedArticles
    }).finally(() => {
      this.articles.length > 0 ? this.onReady() : this.onError()
    })
  }

  private parseLead (markup: string) {
    let html: string
    let obj: HasDriver<ModuleDriver.Paragraph>[]
    try {
      obj = this.DeMarked.convertMDToObjects(markup) as HasDriver<ModuleDriver.Paragraph>[]
      html = this.DeMarked.convertObjectsToHTML(obj)
      this.onReady()
      return html
    } catch (e) {
      this.onError(e)
      return ''
    }
  }
}
